import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const PROCESSED_KEY = "haruki_processed_oauth_code";

/**
 * /auth/callback?code=...&state=...
 *
 * Exchanges Google OAuth code for a Haruki session, links any pending trial intake,
 * then HARD-redirects (window.location.replace) to the role-appropriate landing route.
 *
 * Why a hard redirect: ensures AuthProvider re-mounts with the new session cookie.
 * Single-flight guard via sessionStorage survives StrictMode remount + Fast Refresh.
 */
export default function AuthCallback() {
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    const search = new URLSearchParams(location.search || window.location.search || "");
    const code = search.get("code");
    const state = search.get("state");
    const oauthErr = search.get("error");

    if (oauthErr) {
      setError(`Google sign-in cancelled (${oauthErr}).`);
      setTimeout(() => window.location.replace("/"), 1500);
      return;
    }
    if (!code || !state) {
      window.location.replace("/");
      return;
    }
    if (sessionStorage.getItem(PROCESSED_KEY) === code) return;
    sessionStorage.setItem(PROCESSED_KEY, code);

    (async () => {
      try {
        const redirect_uri = window.location.origin + "/auth/callback";
        const r = await api.post("/auth/session", { code, state, redirect_uri });
        const user = r.data.user;

        // Link pending trial intake, if any
        const intakeId = sessionStorage.getItem("haruki_intake_id");
        if (intakeId) {
          try {
            await api.post("/trial/link", { intake_id: intakeId });
          } catch (linkErr) {
            console.warn("Trial link failed:", linkErr?.response?.data || linkErr?.message);
          } finally {
            sessionStorage.removeItem("haruki_intake_id");
          }
        }
        sessionStorage.removeItem("haruki_signin_email_hint");

        let finalUser = user;
        if (intakeId) {
          try {
            const me = await api.get("/auth/me");
            finalUser = me.data;
          } catch (_) { /* fall through */ }
        }
        let dest = "/role";
        if (!finalUser.role) {
          dest = "/role";
        } else if (finalUser.role === "platform_admin") {
          dest = "/platform";
        } else if (finalUser.role === "agency_admin") {
          dest = "/agency";
        } else if (!finalUser.disclosure_acknowledged_at && finalUser.role === "service_member") {
          dest = "/disclosure";
        } else {
          dest = "/app";
        }
        window.location.replace(dest);
      } catch (e) {
        setError(e?.response?.data?.detail || "Sign-in failed. Please try again.");
        setTimeout(() => window.location.replace("/"), 2000);
      }
    })();
  }, [location.search]);

  return (
    <div className="dark flex min-h-screen items-center justify-center bg-background text-foreground">
      <div className="text-sm text-muted-foreground">{error || "Securing your session…"}</div>
    </div>
  );
}
